import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Key, FilePdf, PaperPlaneTilt, Handshake } from "@phosphor-icons/react";

const empty = { name: "", sender_emails: "", contact_email: "", sop: "", notify_on_close: true, sla_dispatch_minutes: "" };

export async function downloadPdf(url, filename) {
  const r = await api.get(url, { responseType: "blob" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(r.data);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [portal, setPortal] = useState(null);

  const savePortal = async () => {
    if (!portal.email || !portal.password) return toast.error("Portal email and password are required");
    try {
      await api.post(`/clients/${portal.clientId}/portal-user`, { email: portal.email, password: portal.password });
      toast.success("Portal login saved", { description: `${portal.email} can now sign in at /login` });
      setPortal(null);
      load();
    } catch (e) {
      toast.error("Failed to save portal login", { description: e.response?.data?.detail || e.message });
    }
  };

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      sender_emails: form.sender_emails.split(",").map((s) => s.trim()).filter(Boolean),
      sla_dispatch_minutes: form.sla_dispatch_minutes === "" || form.sla_dispatch_minutes === null ? null : parseInt(form.sla_dispatch_minutes, 10),
    };
    try {
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post("/clients", payload);
      toast.success(editId ? "Client updated" : "Client added");
      setForm(null); setEditId(null); load();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    }
  };

  const edit = (c) => {
    setEditId(c.id);
    setForm({ ...c, sender_emails: c.sender_emails.join(", ") });
  };

  const del = async (id) => {
    await api.delete(`/clients/${id}`);
    toast.success("Client removed");
    load();
  };

  const toggleApi = async (c) => {
    const next = !c.security_api_active;
    try {
      await api.patch(`/clients/${c.id}/security`, { active: next });
      setClients((cs) => cs.map((x) => (x.id === c.id ? { ...x, security_api_active: next } : x)));
      toast.success(next ? "CSG dispatch API activated" : "CSG dispatch API disabled — alerts log in Ops only");
    } catch (e) {
      toast.error("Couldn't update API access", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="clients-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading text-4xl font-black tracking-tighter uppercase">API Clients</h1>
          <p className="text-zinc-400 text-sm mt-1">API-integrated partners. Sender emails identify clients on inbound mail. SOPs are auto-inserted into dispatched incidents.</p>
        </div>
        <button onClick={() => { setForm({ ...empty }); setEditId(null); }} className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white flex items-center gap-2" data-testid="add-client-button">
          <Plus size={14} weight="bold" /> Add Client
        </button>
      </div>

      {form && (
        <div className="border border-zinc-800 bg-[#121214] p-6 mt-6 grid gap-4" data-testid="client-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Client Name</label>
              <input className="tactical-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Contact Email (closure notices)</label>
              <input className="tactical-input mt-1" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="client-contact-input" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Sender Emails (comma-separated)</label>
              <input className="tactical-input mt-1 font-mono-data text-xs" value={form.sender_emails} onChange={(e) => setForm({ ...form, sender_emails: e.target.value })} data-testid="client-senders-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">SLA — Dispatch Target (minutes, blank = none)</label>
              <input type="number" min="1" className="tactical-input mt-1 font-mono-data text-xs" value={form.sla_dispatch_minutes ?? ""} onChange={(e) => setForm({ ...form, sla_dispatch_minutes: e.target.value })} data-testid="client-sla-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Standard Operating Procedure (SOP)</label>
            <textarea className="tactical-input mt-1 min-h-[130px] font-mono-data text-xs" value={form.sop} onChange={(e) => setForm({ ...form, sop: e.target.value })} data-testid="client-sop-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={form.notify_on_close} onChange={(e) => setForm({ ...form, notify_on_close: e.target.checked })} data-testid="client-notify-checkbox" />
            Notify client when CSG Connect closes the incident
          </label>
          <div className="flex gap-2">
            <button onClick={save} className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white" data-testid="client-save-button">Save</button>
            <button onClick={() => { setForm(null); setEditId(null); }} className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white">Cancel</button>
          </div>
        </div>
      )}

      <div className="border border-zinc-800 mt-6">
        {clients.map((c) => (
          <div key={c.id} className="flex items-start justify-between p-5 border-b border-zinc-800 last:border-0 bg-[#121214] hover:bg-zinc-900 transition-colors" data-testid={`client-row-${c.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-heading font-bold text-lg">{c.name}</div>
              <div className="font-mono-data text-xs text-[#007AFF] mt-1">{c.sender_emails.join("  ·  ")}</div>
              <div className="text-xs text-zinc-500 mt-2 whitespace-pre-wrap line-clamp-2">{c.sop || "No SOP on file"}</div>
              <div className="font-mono-data text-[11px] mt-2" data-testid={`client-portal-status-${c.id}`}>
                {c.portal_email
                  ? <span className="text-green-400">PORTAL LOGIN: {c.portal_email}</span>
                  : <span className="text-zinc-600">NO PORTAL LOGIN</span>}
              </div>
              <button onClick={() => toggleApi(c)} data-testid={`client-api-toggle-${c.id}`}
                className={`mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-bold font-mono-data border transition-colors ${c.security_api_active ? "border-green-500/40 bg-green-500/10 text-green-400 hover:bg-green-500/20" : "border-zinc-600 bg-zinc-800/60 text-zinc-400 hover:bg-zinc-700"}`}>
                <span className={`w-2 h-2 rounded-full ${c.security_api_active ? "bg-green-400" : "bg-zinc-500"}`} />
                {c.security_api_active ? "CSG DISPATCH: ACTIVE" : "CSG DISPATCH: OFF (alerts only)"}
              </button>
              {portal?.clientId === c.id && (
                <div className="grid sm:grid-cols-3 gap-2 mt-3 max-w-xl" data-testid={`portal-form-${c.id}`}>
                  <input className="tactical-input font-mono-data text-xs" placeholder="portal login email" value={portal.email} onChange={(e) => setPortal({ ...portal, email: e.target.value })} data-testid="portal-email-input" />
                  <input className="tactical-input font-mono-data text-xs" placeholder="password" type="text" value={portal.password} onChange={(e) => setPortal({ ...portal, password: e.target.value })} data-testid="portal-password-input" />
                  <div className="flex gap-2">
                    <button onClick={savePortal} className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white" data-testid="portal-save-button">Save</button>
                    <button onClick={() => setPortal(null)} className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white">Cancel</button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0 ml-4">
              <button onClick={() => api.post(`/clients/${c.id}/send-onboarding`).then((r) => toast.success(`Onboarding email sent to ${r.data.to}`, { description: r.data.portal_password_reset ? `Portal password reset & included in email` : (r.data.portal_email ? `Portal user: ${r.data.portal_email}` : undefined) })).catch((e) => toast.error("Onboarding email failed", { description: e.response?.data?.detail || e.message }))} title="Send onboarding email (includes portal login + fresh password if portal exists)" className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm text-yellow-400" data-testid={`client-onboarding-${c.id}`}><Handshake size={16} /></button>
              <button onClick={() => api.post(`/reports/client/${c.id}/email`).then((r) => toast.success(`Report emailed to ${r.data.to}`)).catch((e) => toast.error("Email failed", { description: e.response?.data?.detail || e.message }))} title="Email report to client" className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm text-green-400" data-testid={`client-email-report-${c.id}`}><PaperPlaneTilt size={16} /></button>
              <button onClick={() => downloadPdf(`/reports/client/${c.id}`, `resqconnect-report-${c.name.toLowerCase().replace(/ /g, "-")}.pdf`).then(() => toast.success("Report downloaded")).catch(() => toast.error("Report download failed"))} title="Download PDF report" className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm text-red-400" data-testid={`client-report-${c.id}`}><FilePdf size={16} /></button>
              <button onClick={() => setPortal({ clientId: c.id, email: c.portal_email || "", password: "" })} title="Portal login" className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm text-[#007AFF]" data-testid={`client-portal-${c.id}`}><Key size={16} /></button>
              <button onClick={() => edit(c)} className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm" data-testid={`client-edit-${c.id}`}><PencilSimple size={16} /></button>
              <button onClick={() => del(c.id)} className="p-2 border border-zinc-800 hover:bg-red-500/20 text-red-400 rounded-sm" data-testid={`client-delete-${c.id}`}><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
